import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
} from "react";
import useFetchDomains from "../../../hooks/useFetchDomains";
import { Autocomplete, TextField } from "@mui/material";

interface IAutoCompleteDomainList {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handelOnChangeDomain: (event: ChangeEvent<any>, value: any) => void;
  currentDomainId: string | number | null | any[];
  sx?: React.CSSProperties;
  className?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string;
  listLabel?: string;
  placeholder?: string;
  includeDomainId?: boolean; // ✅ NEW: Controls whether to include domain ID in display
  multiple?: boolean; // ✅ NEW: Controls whether to allow multiple selection
  lazyLoad?: boolean; // ✅ NEW: Controls whether to fetch data on mount or on dropdown open
}

/**
 * All domains list with auto complete dropdown functionality
 * @returns
 * Selected domain with id and name
 */
const AutoCompleteDomainList: React.FC<IAutoCompleteDomainList> = ({
  setIsLoading,
  handelOnChangeDomain,
  currentDomainId,
  sx,
  className,
  isDisabled = false,
  required,
  error,
  helperText,
  listLabel,
  placeholder,
  includeDomainId = false, // ✅ NEW: Default to true to maintain existing behavior
  multiple = false, // ✅ NEW: Default to false for single selection
  lazyLoad = false, // ✅ NEW: Default to false for backward compatibility
}) => {
  // Track if dropdown has been opened (for lazy loading)
  const [hasOpened, setHasOpened] = useState(false);

  const [domainsData, fetchDomains, isFetching] = useFetchDomains({
    setIsLoading,
    fetchOnMount: !lazyLoad,
  });

  // Handle dropdown open - trigger fetch if lazy loading
  const handleOpen = useCallback(() => {
    if (lazyLoad && !hasOpened) {
      setHasOpened(true);
      fetchDomains();
    }
  }, [lazyLoad, hasOpened, fetchDomains]);

  // Show loading spinner while fetching data
  const isLoading = lazyLoad && (isFetching || !hasOpened);

  // Determine value based on multiple prop
  const getValue = () => {
    if (multiple) {
      // For multiple selection, currentDomainId should be an array
      if (Array.isArray(currentDomainId)) {
        return (domainsData || []).filter((option) =>
          currentDomainId.some((id) => id === option.id || id?.id === option.id)
        );
      }
      return [];
    } else {
      // For single selection
      return (
        (domainsData || []).find((option) => option.id === currentDomainId) ||
        null
      );
    }
  };

  return (
    <Autocomplete
      sx={sx}
      multiple={multiple}
      options={domainsData || []}
      value={getValue()}
      getOptionLabel={(option) => {
        return includeDomainId
          ? `(${option.id}) ${option.domain_name}` || ""
          : option.domain_name || "";
      }}
      onOpen={handleOpen}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          style={{ color: "#000000" }}
          {...params}
          label={
            <span>
              {listLabel ? listLabel : "Domain"}
              {required ? <span className="required-asterisk">*</span> : ""}
            </span>
          }
          // required={required}
          placeholder={placeholder ? placeholder : "Select..."}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!error}
          helperText={helperText || ""}
        />
      )}
      className={className}
      loadingText="Loading..."
      onChange={(event, value) => handelOnChangeDomain(event, value)}
      disabled={Boolean(isDisabled)}
    />
  );
};

export default AutoCompleteDomainList;
